import {
  CircleCheckBig,
  FileSearch,
  UserCheck,
  UserStar,
  UserX,
  Clock,
  Handshake,
} from "lucide-react";

const DEFAULT_ACTION_META = {
  label: "Status",
  icon: CircleCheckBig,
  iconColor: "#94A3B8",
  bgLight: "linear-gradient(90deg, #F5F5F5, #D4D4D4)",
  bgDark: "linear-gradient(90deg, #363636, #6B7280)",
  border: "rgba(148,163,184,0.30)",
};

const STATUS_CODE_META = {
  APPLIED: {
    label: "Applied",
    icon: CircleCheckBig,
    iconColor: "#7997C3",
    bgLight: "linear-gradient(90deg, #F9F9F9, #A6BEE0)",
    bgDark: "linear-gradient(90deg, #363636, #7997c3)",
    border: "rgba(59,130,246,0.3)",
  },
  SHORTLISTED: {
    label: "Shortlist",
    icon: UserCheck,
    iconColor: "#A786B4",
    bgLight: "linear-gradient(90deg, #F0F0F0, #A786B4)",
    bgDark: "linear-gradient(90deg, #363636, #a786b4)",
    border: "rgba(168,85,247,0.3)",
  },
  INTERVIEWED: {
    label: "Interview",
    icon: FileSearch,
    iconColor: "#C0A783",
    bgLight: "linear-gradient(90deg, #F0F0F0, #DDCD90)",
    bgDark: "linear-gradient(90deg, #363636, #c0a783)",
    border: "rgba(99,102,241,0.3)",
  },
  HIRED: {
    label: "Hire",
    icon: UserStar,
    iconColor: "#87AA92",
    bgLight: "linear-gradient(90deg, #F0F0F0, #87AA92)",
    bgDark: "linear-gradient(90deg, #363636, #87aa92)",
    border: "rgba(34,197,94,0.3)",
  },
  REJECTED: {
    label: "Reject",
    icon: UserX,
    iconColor: "#C4A1A1",
    bgLight: "linear-gradient(90deg, #F0F0F0, #C4A1A1)",
    bgDark: "linear-gradient(90deg, #363636, #c4a1a1)",
    border: "rgba(239,68,68,0.3)",
  },
  OFFERED: {
    label: "Offer",
    icon: Handshake,
    iconColor: "#D4A24C",
    bgLight: "linear-gradient(90deg, #F7F4EC, #E8C98C)",
    bgDark: "linear-gradient(90deg, #363636, #B0893E)",
    border: "rgba(212,162,76,0.3)",
  },
  ONHOLD: {
    label: "On Hold",
    icon: Clock,
    iconColor: "#9CA3AF",
    bgLight: "linear-gradient(90deg, #F3F4F6, #D1D5DB)",
    bgDark: "linear-gradient(90deg, #363636, #9CA3AF)",
    border: "rgba(156,163,175,0.3)",
  },
};

const STATUS_TYPE_META = {
  INITIAL: {
    label: "Initial",
    icon: CircleCheckBig,
    iconColor: "#7997C3",
    bgLight: "linear-gradient(90deg, #F9F9F9, #A6BEE0)",
    bgDark: "linear-gradient(90deg, #363636, #7997c3)",
    border: "rgba(59,130,246,0.3)",
  },
  INTERMEDIATE: {
    label: "In Progress",
    icon: FileSearch,
    iconColor: "#A1A1AA",
    bgLight: "linear-gradient(90deg, #F5F5F5, #D4D4D8)",
    bgDark: "linear-gradient(90deg, #363636, #71717A)",
    border: "rgba(161,161,170,0.3)",
  },
  HIRED: {
    label: "Hired",
    icon: UserStar,
    iconColor: "#87AA92",
    bgLight: "linear-gradient(90deg, #F0F0F0, #87AA92)",
    bgDark: "linear-gradient(90deg, #363636, #87aa92)",
    border: "rgba(34,197,94,0.3)",
  },
  REJECTED: {
    label: "Rejected",
    icon: UserX,
    iconColor: "#C4A1A1",
    bgLight: "linear-gradient(90deg, #F0F0F0, #C4A1A1)",
    bgDark: "linear-gradient(90deg, #363636, #c4a1a1)",
    border: "rgba(239,68,68,0.3)",
  },
};

export const getStatusType = (status) => {
  if (!status || typeof status === "string") return "INTERMEDIATE";
  if (status?.isInitial) return "INITIAL";
  if (status?.isHired) return "HIRED";
  if (status?.isRejected) return "REJECTED";
  return "INTERMEDIATE";
};

// How a candidate status is read off whatever shape it arrives in: the
// tenant's definition (`{ id, code, name }`), the legacy code the store holds
// (`"OFFERED"`), or nothing at all. Every reader on this screen shares these,
// so "Offered" and "OFFERED" cannot end up next to each other on it.
export const getStatusValue = (status) => {
  if (!status) return "";
  if (typeof status === "string") return status;
  return status.code || status.name || status.status || "";
};

// A string is the status's name as the tenant wrote it — the detail route
// sends `status_def.name` — so it prints untouched. Re-casing it turned
// "Interview Round 2" into "Interview round 2".
export const getStatusLabel = (status, fallback = "No Status") => {
  if (!status) return fallback;

  if (typeof status === "string") return status;

  return status.name || status.label || status.code || fallback;
};

export const getActionMeta = (status) => {
  const code = getStatusValue(status)?.toUpperCase?.() || "";
  const type = getStatusType(status);

  if (code && STATUS_CODE_META[code]) {
    return {
      ...DEFAULT_ACTION_META,
      ...STATUS_CODE_META[code],
      label: getStatusLabel(status),
    };
  }

  if (type && STATUS_TYPE_META[type]) {
    return {
      ...DEFAULT_ACTION_META,
      ...STATUS_TYPE_META[type],
      label: getStatusLabel(status),
    };
  }

  return {
    ...DEFAULT_ACTION_META,
    label: getStatusLabel(status),
  };
};

/**
 * Backward-compatible legacy export for components
 * still importing ACTIONS.
 */
export const ACTIONS = Object.entries(STATUS_CODE_META).map(([code, meta]) => ({
  value: code,
  key: code,
  code,
  label: meta.label,
  name: meta.label,
  icon: meta.icon,
  iconColor: meta.iconColor,
  bgLight: meta.bgLight,
  bgDark: meta.bgDark,
  border: meta.border,
}));

export default ACTIONS;